import { Box, Flex } from "rebass/styled-components";
import styled from "styled-components";

import { MdArrowUpward } from "react-icons/md";
import { BsEmojiSmile } from "react-icons/bs";
import { EmojiSelector } from "MVVM/Components/EmoijSelector";
import { useClipCommentForm } from "MVVM/Hooks/useClipCommentForm";

interface IClipCommentFormProps {
  clipId: string;
}

const ClipCommentForm = ({ clipId }: IClipCommentFormProps) => {
  const {
    loading,
    inputRef,
    disabled,
    handleFocus,
    handleKeyDown,
    handleInput,
    handlePaste,
    handleClick,
    handleEmojiSelect,
  } = useClipCommentForm({ clipId });

  return (
    <ClipCommentFormWrapper>
      <InputWrapper loading={loading} onFocus={handleFocus}>
        <Input
          ref={inputRef}
          role="textbox"
          contentEditable={!loading}
          onKeyDown={handleKeyDown}
          aria-multiline="false"
          onInput={handleInput}
          onPaste={handlePaste}
          data-placeholder="Add a comment..."
        />
        <ButtonContainer>
          <EmojiSelector
            onEmojiSelect={handleEmojiSelect}
            icon={BsEmojiSmile}
          />
          <StyledButton
            onClick={disabled ? undefined : handleClick}
            disabled={disabled}
          >
            <StyledMdArrowUpward />
          </StyledButton>
        </ButtonContainer>
      </InputWrapper>
    </ClipCommentFormWrapper>
  );
};

const InputWrapper = styled(Flex)<{ loading: boolean }>`
  align-items: flex-end;
  background-color: ${({ theme }) => theme.colors.grey850};
  border-radius: 8px;
  border: none;
  color: ${({ loading, theme }) =>
    loading ? theme.colors.chalk : theme.colors.ice};
  cursor: ${({ loading }) => (loading ? "not-allowed" : "text")};
  max-width: 100%;
  min-height: 56px;
  padding: 14px 16px;
  width: 100%;
`;

const Input = styled(Box)`
  align-self: center;
  display: block;
  flex-grow: 1;
  flex-shrink: 1;
  min-width: 0;
  font-size: 16px;
  line-height: 1.4em;
  font-weight: 500;
  outline: none;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-height: 5.6em;
  overflow-y: auto;

  :empty::before {
    color: ${({ theme }) => theme.colors.chalkAlpha80};
    content: attr(data-placeholder);
  }

  :empty:focus::before {
    content: "";
  }

  br {
    display: none;
  }
`;

const ButtonContainer = styled(Flex)`
  align-items: center;
  align-self: center;
  flex-shrink: 0;
  flex-grow: 0;
  gap: 8px;
  margin-left: 8px;
`;

const StyledButton = styled(Flex)<{ disabled: boolean }>`
  align-items: center;
  align-self: center;
  background: ${(props) =>
    props.disabled ? props.theme.colors.ned : props.theme.colors.envy};
  border-radius: 99px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  flex-grow: 0;
  flex-shrink: 0;
  height: 28px;
  justify-content: center;
  line-height: 0;
  padding: 0;
  width: 28px;

  :hover {
    background: ${(props) =>
      props.disabled ? props.theme.colors.ned : props.theme.colors.envyAlpha80};
  }
`;

const StyledMdArrowUpward = styled(MdArrowUpward)`
  height: 16px;
  width: 16px;
  color: ${({ theme }) => theme.colors.ice};
`;

const ClipCommentFormWrapper = styled(Flex)`
  background: ${({ theme }) => theme.colors.ned};
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  padding: 24px;
  position: static;
`;

export { ClipCommentForm };
